import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import {
  QTYPES,
  emptyForm,
  emptyPrompt,
  emptyQuestion,
  formFromTemplate,
  payloadFromForm,
  templateAccess,
} from "./meetingTemplateForm";

function jump(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function QuestionRow({ q, i, lang, t, setQuestion, move, remove, canMoveDown, testPrefix }) {
  const label = q.label?.[lang] || "";
  const setLabel = (v) => setQuestion(i, { label: { ...q.label, [lang]: v } });
  const needsOptions = q.type === "single_choice" || q.type === "multi_choice" || q.type === "checklist";
  return (
    <div className="tb-tpl-q" data-testid={`${testPrefix}-${i}`}>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={t("settings.templates.label")}
        data-testid={`${testPrefix}-label-${i}`}
      />
      <select value={q.type} onChange={(e) => setQuestion(i, { type: e.target.value })}>
        {QTYPES.map((k) => (
          <option key={k} value={k}>{t(`settings.templates.qtype.${k}`)}</option>
        ))}
      </select>
      <label className="tb-tpl-req">
        <input type="checkbox" checked={!!q.required} onChange={(e) => setQuestion(i, { required: e.target.checked })} />
        {t("settings.templates.required")}
      </label>
      <div className="tb-tpl-q-actions">
        <button type="button" className="tb-ghost-btn" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
        <button type="button" className="tb-ghost-btn" onClick={() => move(i, 1)} disabled={!canMoveDown}>↓</button>
        <button type="button" className="tb-ghost-btn" onClick={() => remove(i)}>{t("settings.templates.removeQuestion")}</button>
      </div>
      {needsOptions && (
        <input
          className="tb-tpl-q-options"
          value={(q.options || []).join(", ")}
          onChange={(e) => setQuestion(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          placeholder={t("settings.templates.options")}
        />
      )}
    </div>
  );
}

export default function MeetingTemplateBuilder() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canAdvisory, canTeam, canList } = templateAccess(user);
  const typeParam = params.get("type");
  const initialType = typeParam === "advisory" && canAdvisory ? "advisory" : (canTeam ? "internal_1_1" : "advisory");
  const isNew = !id;
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(isNew ? emptyForm(initialType) : null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canList || isNew) return;
    api.get("/meeting-templates")
      .then((r) => {
        const found = (r.data || []).find((x) => x.id === id);
        if (!found) {
          setErr(t("settings.templates.notFound"));
          return;
        }
        if (found.is_system) {
          navigate("/app/settings/templates", { replace: true });
          return;
        }
        setMeta(found);
        setForm(formFromTemplate(found));
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [canList, id, isNew, t]);

  if (!canList) {
    return (
      <div className="tb-page">
        <h1 className="tb-h1">{t("settings.templates.title")}</h1>
        <p className="tb-muted">{t("settings.templates.principalOnly")}</p>
      </div>
    );
  }
  if (!form) return <div className="tb-loading">{t("common.loading")}</div>;

  const langLabel = lang === "fr" ? "Français" : "English";
  const listUrl = `/app/settings/templates?type=${form.session_type || initialType}`;
  const isSystem = !!(meta && meta.is_system);

  const patchList = (key, i, patch) => {
    setForm((f) => ({ ...f, [key]: f[key].map((row, idx) => (idx === i ? { ...row, ...patch } : row)) }));
  };
  const moveList = (key, i, dir) => {
    setForm((f) => {
      const j = i + dir;
      if (j < 0 || j >= f[key].length) return f;
      const rows = [...f[key]];
      [rows[i], rows[j]] = [rows[j], rows[i]];
      return { ...f, [key]: rows };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const payload = payloadFromForm(form);
      if (id && meta && !meta.is_system) {
        await api.put(`/meeting-templates/${id}`, payload);
      } else {
        const r = await api.post("/meeting-templates", payload);
        setMeta(r.data);
        setForm(formFromTemplate(r.data));
        navigate(`/app/settings/templates/build/${r.data.id}`, { replace: true });
      }
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!id || isSystem) return;
    if (!window.confirm(t("settings.templates.confirmDelete"))) return;
    try {
      await api.delete(`/meeting-templates/${id}`);
      navigate(listUrl);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  return (
    <div className="tb-page" data-testid="template-builder">
      <Link to={listUrl} className="tb-muted" style={{ fontSize: "0.85rem" }}>← {t("common.back")}</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
        <h1 className="tb-h1">{isNew ? t("settings.templates.titleNew") : t("settings.templates.titleEdit")}</h1>
        <span className="tb-badge accent">{t("fwBuilder.editingIn")} {langLabel}</span>
      </div>
      <p className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 4 }}>{t("fwBuilder.langHint")}</p>
      {isSystem && <p className="tb-muted" style={{ fontSize: "0.82rem" }}>{t("settings.templates.systemCopyHint")}</p>}

      <nav className="tb-jump-nav" data-testid="template-jump">
        {[
          ["tpl-basics", t("settings.templates.jumpBasics")],
          ["tpl-before", t("settings.templates.jumpBefore")],
          ["tpl-during", t("settings.templates.jumpDuring")],
          ["tpl-after", t("settings.templates.jumpAfter")],
          ["tpl-ai", t("settings.templates.jumpAi")],
        ].map(([sid, label]) => (
          <button key={sid} type="button" className="tb-ghost-btn" onClick={() => jump(sid)}>{label}</button>
        ))}
      </nav>

      <form className="tb-form" onSubmit={save} data-testid="template-editor">
        <div className="tb-block" id="tpl-basics">
          <h3 style={{ marginTop: 0 }}>{t("settings.templates.jumpBasics")}</h3>
          <div>
            <label>{t("settings.templates.name")}</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name" />
          </div>
          <div>
            <label>{t("settings.templates.description")}</label>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <label style={{ display: "flex", gap: 8, fontWeight: 400 }}>
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
            {t("settings.templates.markDefault")}
          </label>
        </div>

        <div className="tb-block" id="tpl-before" style={{ marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t("settings.templates.beforeTitle")}</h3>
          {form.questions.map((q, i) => (
            <QuestionRow
              key={`b-${i}`}
              q={q}
              i={i}
              lang={lang}
              t={t}
              testPrefix="question"
              setQuestion={(idx, patch) => patchList("questions", idx, patch)}
              move={(idx, dir) => moveList("questions", idx, dir)}
              remove={(idx) => setForm((f) => ({ ...f, questions: f.questions.filter((_, j) => j !== idx) }))}
              canMoveDown={i < form.questions.length - 1}
            />
          ))}
          <button type="button" className="tb-ghost-btn" onClick={() => setForm((f) => ({ ...f, questions: [...f.questions, emptyQuestion(f.questions.length)] }))}>
            {t("settings.templates.addQuestion")}
          </button>
        </div>

        <div className="tb-block" id="tpl-during" style={{ marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t("settings.templates.duringTitle")}</h3>
          <p className="tb-muted" style={{ marginTop: 0 }}>{t("settings.templates.duringHint")}</p>
          {(form.during_note_prompts || []).map((p, i) => (
            <div className="tb-tpl-q" key={`d-${i}`} data-testid={`during-prompt-${i}`}>
              <input
                value={p.label?.[lang] || ""}
                onChange={(e) => {
                  const rows = [...form.during_note_prompts];
                  rows[i] = { ...p, label: { ...p.label, [lang]: e.target.value } };
                  setForm({ ...form, during_note_prompts: rows });
                }}
                placeholder={t("settings.templates.prompt")}
              />
              <button
                type="button"
                className="tb-ghost-btn"
                onClick={() => setForm((f) => ({ ...f, during_note_prompts: f.during_note_prompts.filter((_, j) => j !== i) }))}
              >
                {t("settings.templates.removeQuestion")}
              </button>
            </div>
          ))}
          <button
            type="button"
            className="tb-ghost-btn"
            onClick={() => setForm((f) => ({ ...f, during_note_prompts: [...(f.during_note_prompts || []), emptyPrompt((f.during_note_prompts || []).length)] }))}
            data-testid="add-during-prompt"
          >
            {t("settings.templates.addDuringPrompt")}
          </button>
        </div>

        <div className="tb-block" id="tpl-after" style={{ marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t("settings.templates.afterTitle")}</h3>
          <p className="tb-muted" style={{ marginTop: 0 }}>{t("settings.templates.afterHint")}</p>
          {form.after_questions.map((q, i) => (
            <QuestionRow
              key={`a-${i}`}
              q={q}
              i={i}
              lang={lang}
              t={t}
              testPrefix="after-question"
              setQuestion={(idx, patch) => patchList("after_questions", idx, patch)}
              move={(idx, dir) => moveList("after_questions", idx, dir)}
              remove={(idx) => setForm((f) => ({ ...f, after_questions: f.after_questions.filter((_, j) => j !== idx) }))}
              canMoveDown={i < form.after_questions.length - 1}
            />
          ))}
          <button
            type="button"
            className="tb-ghost-btn"
            onClick={() => setForm((f) => ({ ...f, after_questions: [...f.after_questions, emptyQuestion(f.after_questions.length)] }))}
            data-testid="add-after-question"
          >
            {t("settings.templates.addAfterQuestion")}
          </button>
        </div>

        <div className="tb-block" id="tpl-ai" style={{ marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t("settings.templates.summaryTitle")}</h3>
          <p className="tb-muted" style={{ marginTop: 0 }}>{t("settings.templates.summaryHint")}</p>
          <textarea
            rows={8}
            value={form.summary_direction_md}
            onChange={(e) => setForm({ ...form, summary_direction_md: e.target.value })}
            placeholder={t("settings.templates.summaryPlaceholder")}
            data-testid="summary-direction-md"
            style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.88rem" }}
          />
        </div>

        {err && <div className="tb-form-error" style={{ marginTop: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          <button type="submit" className="tb-primary-btn" disabled={saving} data-testid="save-template">
            {saving ? t("auth.loading") : t("common.save")}
          </button>
          <Link to={listUrl} className="tb-ghost-btn" style={{ textDecoration: "none" }}>{t("book.cancel")}</Link>
          {id && !isSystem && (
            <button type="button" className="tb-danger-btn" onClick={remove}>{t("settings.templates.delete")}</button>
          )}
        </div>
      </form>
    </div>
  );
}
